import React, {useState} from "react";
import PropTypes from "prop-types";
import {
  AButton,
  AModal,
  APanel,
  APanelBody,
  APanelFooter,
  APanelHeader,
  APanelTitle
} from "@cisco-sbg-ui/magna-react";
import {t} from "i18next";
import {useSelector} from "react-redux";
import MarkdownEditor from "components/MarkdownEditor";
import {selectIncidentDetailShortId} from "store/selectors";
import theme from "theme/index.scss";

const MarkdownModal = ({
  description,
  isOpen = false,
  defaultValue,
  onSubmit,
  setIsOpen,
  setTelemetry,
  extraTelemetryValues = [],
  testId = "Description",
  title,
  showEditorOnLoad = false
}) => {
  const incidentId = useSelector(selectIncidentDetailShortId);
  const [originalValue, setOriginalValue] = useState(defaultValue);
  const [showEditor, setShowEditor] = useState(showEditorOnLoad);
  const [value, setValue] = useState(defaultValue);
  const handleTelemetry = setTelemetry || null;

  const handleSubmit = () => {
    onSubmit?.(value);
    setShowEditor(false);
    setOriginalValue(value);
  };

  const handleClose = () => {
    setShowEditor(false);
    setValue(originalValue);
    setIsOpen(false);
  };

  const telemetryButtonClick = (buttonName, onClick) => {
    if (handleTelemetry) {
      handleTelemetry(incidentId, buttonName, ...extraTelemetryValues);
    }
    onClick();
  };

  const FooterButton = ({name, onClick, ...rest}) => (
    <AButton
      medium
      className="mt-4 ml-2"
      onClick={() => telemetryButtonClick(name, onClick)}
      {...rest}>
      {name}
    </AButton>
  );

  const renderSubmitButton = (
    <FooterButton
      data-testid={`${testId}-MarkdownModal-Button-save`}
      onClick={handleSubmit}
      name={t("misc.save")}
      disabled={!value || value === originalValue}
      primary
    />
  );

  const renderEditButton = (
    <FooterButton
      data-testid={`${testId}-MarkdownModal-Button-edit`}
      name={t("misc.edit")}
      onClick={() => setShowEditor(true)}
      secondary
    />
  );

  return (
    <AModal
      xlarge
      aria-labelledby="modalTitle"
      isOpen={isOpen}
      data-testid={`${testId}-MarkdownModal`}>
      <APanel type="dialog">
        <APanelHeader className="flex-column">
          {title && (
            <APanelTitle id="modalTitle" className="align-self-start">
              {title}
            </APanelTitle>
          )}
          {description && (
            <div className="mt-2" style={{color: theme.secondaryText}}>
              {description}
            </div>
          )}
        </APanelHeader>
        <APanelBody>
          <MarkdownEditor
            isModal
            active={showEditor}
            defaultValue={defaultValue}
            minEditorHeight={200}
            value={value}
            onChange={(newValue) => setValue(newValue)}
            usePlaceholderText
          />
        </APanelBody>
        <APanelFooter>
          <FooterButton
            data-testid={`${testId}-MarkdownModal-Button-close`}
            name={t("misc.close")}
            onClick={handleClose}
            tertiary
          />
          {showEditor ? renderSubmitButton : renderEditButton}
        </APanelFooter>
      </APanel>
    </AModal>
  );
};

export default MarkdownModal;

MarkdownModal.propTypes = {
  /**
   * Option to pass a description to be shown under the modal title
   */
  description: PropTypes.string,
  /**
   * Option to pass additional telemetry values to the telemetry function
   *
   * By default, incidentId and buttonName are already passed to the telemetry
   * function
   */
  extraTelemetryValues: PropTypes.array,
  /**
   * Open state for the modal
   *
   * @defaultValue false
   */
  isOpen: PropTypes.bool,
  /**
   * Value that will be prepopulated into the markdown component
   */
  defaultValue: PropTypes.string,
  /**
   * Function to run when saving the markdown
   */
  onSubmit: PropTypes.func,
  /**
   * Function to set the open state of the modal
   */
  setIsOpen: PropTypes.func,
  /**
   * Function to send telemetry
   */
  setTelemetry: PropTypes.func,
  /**
   * Prefix to be used in data-testid's
   *
   * @defaultValue "Description"
   */
  testId: PropTypes.string,
  /**
   * Title for the modal
   */
  title: PropTypes.string,
  /**
   * True if the edit view should be active on load
   *
   * @defaultValue false
   */
  showEditorOnLoad: PropTypes.bool
};
